import React, { FormEvent, useEffect, useState } from "react";
import { User } from "firebase/auth";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { BadgeCheck, Camera, Mail, MapPin, Save, ShieldCheck, UserRound } from "lucide-react";
import { db } from "../../Lib/Firebase";

export type AdminProfile = {
    name?: string;
    phone?: string;
    title?: string;
    dateOfBirth?: string;
    location?: string;
    photoUrl?: string;
    bio?: string;
};

const emptyProfile: AdminProfile = {
    name: "",
    phone: "",
    title: "Administrator",
    dateOfBirth: "",
    location: "",
    photoUrl: "",
    bio: "",
};

export default function AdminProfileTab({ user, onSaved }: { user: User | null; onSaved?: (profile: AdminProfile) => void }) {
    const [profile, setProfile] = useState<AdminProfile>(emptyProfile);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        const email = user?.email?.toLowerCase();
        if (!email) {
            setLoading(false);
            return;
        }
        let active = true;
        getDoc(doc(db, "admins", email))
            .then((snapshot) => {
                if (!active) return;
                const data = snapshot.data() || {};
                const next = {
                    ...emptyProfile,
                    name: data.name || user?.displayName || "",
                    phone: data.phone || "",
                    title: data.title || "Administrator",
                    dateOfBirth: data.dateOfBirth || "",
                    location: data.location || "",
                    photoUrl: data.photoUrl || user?.photoURL || "",
                    bio: data.bio || "",
                };
                setProfile(next);
                onSaved?.(next);
            })
            .catch(() => setMessage("Your administrator profile could not be loaded."))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [user, onSaved]);

    const updateField = (field: keyof AdminProfile, value: string) => {
        setProfile((current) => ({ ...current, [field]: value }));
        setMessage("");
    };

    const saveProfile = async (event: FormEvent) => {
        event.preventDefault();
        const email = user?.email?.toLowerCase();
        if (!email) return;
        setSaving(true);
        setMessage("");
        try {
            const cleanProfile = Object.fromEntries(
                Object.entries(profile).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
            ) as AdminProfile;
            await updateDoc(doc(db, "admins", email), {
                ...cleanProfile,
                profileUpdatedAt: serverTimestamp(),
            });
            setProfile(cleanProfile);
            onSaved?.(cleanProfile);
            setMessage("Administrator profile updated successfully.");
        } catch (error: any) {
            setMessage(error?.message || "Your profile could not be updated.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="rounded-3xl border border-blue-100 bg-white p-8 text-sm text-slate-500">Loading administrator profile…</div>;

    const initials = (profile.name || user?.email || "A").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();

    return (
        <form onSubmit={saveProfile} className="grid gap-5 xl:grid-cols-[320px,minmax(0,1fr)]">
            <aside className="rounded-3xl border border-blue-100 bg-white p-6 shadow-xl shadow-blue-900/5">
                <div className="mx-auto grid h-28 w-28 place-items-center overflow-hidden rounded-3xl bg-gradient-to-br from-[#075dcc] to-[#38aee8] text-3xl font-black text-white shadow-xl shadow-blue-500/20">
                    {profile.photoUrl ? <img src={profile.photoUrl} alt="Administrator profile" className="h-full w-full object-cover" /> : initials}
                </div>
                <div className="mt-5 text-center">
                    <h2 className="text-xl font-black text-[#061a4a]">{profile.name || "MockZo Administrator"}</h2>
                    <p className="mt-1 text-sm font-semibold text-blue-700">{profile.title || "Administrator"}</p>
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700"><BadgeCheck className="h-4 w-4" /> Verified administrator</div>
                </div>
                <div className="mt-6 space-y-3 border-t border-slate-100 pt-5 text-sm text-slate-600">
                    <div className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /><span className="break-all">{user?.email}</span></div>
                    {profile.location && <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" /><span>{profile.location}</span></div>}
                </div>
                <div className="mt-6 rounded-2xl bg-blue-50 p-4 text-xs leading-5 text-blue-800">
                    <div className="mb-1 flex items-center gap-2 font-black"><ShieldCheck className="h-4 w-4" /> Privacy protected</div>
                    Only you can read or update this administrator profile. Your login email and access permission cannot be changed here.
                </div>
            </aside>

            <section className="rounded-3xl border border-blue-100 bg-white p-5 shadow-xl shadow-blue-900/5 sm:p-7">
                <div className="mb-6 flex items-center gap-3">
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-700"><UserRound className="h-5 w-5" /></span>
                    <div><h2 className="text-xl font-black text-[#061a4a]">Personal information</h2><p className="text-sm text-slate-500">Keep your administrator contact and profile details current.</p></div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Full name"><input value={profile.name} onChange={(e) => updateField("name", e.target.value)} maxLength={80} placeholder="Your full name" /></Field>
                    <Field label="Role / title"><input value={profile.title} onChange={(e) => updateField("title", e.target.value)} maxLength={80} placeholder="Operations Administrator" /></Field>
                    <Field label="Phone number"><input value={profile.phone} onChange={(e) => updateField("phone", e.target.value)} maxLength={20} inputMode="tel" placeholder="+91 98765 43210" /></Field>
                    <Field label="Date of birth (optional)"><input value={profile.dateOfBirth} onChange={(e) => updateField("dateOfBirth", e.target.value)} type="date" /></Field>
                    <Field label="Location (optional)"><input value={profile.location} onChange={(e) => updateField("location", e.target.value)} maxLength={100} placeholder="Hyderabad, India" /></Field>
                    <Field label="Profile photo URL (optional)"><div className="relative"><Camera className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" /><input className="pl-10" value={profile.photoUrl} onChange={(e) => updateField("photoUrl", e.target.value)} type="url" placeholder="https://…" /></div></Field>
                    <Field label="Administrator email" hint="Login identity cannot be edited here."><input value={user?.email || ""} disabled /></Field>
                    <Field label="Short bio (optional)" wide><textarea value={profile.bio} onChange={(e) => updateField("bio", e.target.value)} maxLength={300} rows={4} placeholder="A short introduction for the MockZo team…" /></Field>
                </div>

                <div className="mt-6 flex flex-col-reverse items-stretch justify-between gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center">
                    <p role="status" className={`text-sm font-semibold ${/successfully/i.test(message) ? "text-emerald-700" : "text-rose-600"}`}>{message}</p>
                    <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#075dcc] to-[#38aee8] px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-500/20 transition hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-60"><Save className="h-4 w-4" />{saving ? "Saving…" : "Save profile"}</button>
                </div>
            </section>
        </form>
    );
}

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: React.ReactElement<any> }) {
    return <label className={`block ${wide ? "sm:col-span-2" : ""}`}><span className="mb-2 block text-sm font-bold text-slate-700">{label}</span>{React.cloneElement(children, { className: `${children.props.className || ""} w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400` })}{hint && <span className="mt-1.5 block text-xs text-slate-400">{hint}</span>}</label>;
}
